import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { startOfWeek, endOfWeek, addWeeks, format } from 'date-fns';
import { SpiceLevelPicker } from './MenuPreferencesFields';
import './MenuPreferencesFields.css';
import './MealPlanifyModal.css';

const MealPlanifyModalPreview = ({ isOpen, onClose }) => {
  const [preferences] = useState({
    dietaryPreferences: ['vegan'],
    allergies: ['nuts'],
    costRange: 'medium',
    mealsToConsider: ['breakfast', 'lunch', 'dinner'],
    daysToPlan: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    selectedWeeks: [0],
    cuisinePreferences: ['italian', 'mexican'],
    spiceLevel: 3,
    mealPrepRecipes: false,
    lowCarb: false,
    highProtein: true
  });

  const dietaryOptions = [
    'Vegan', 'Vegetarian', 'Pescatarian', 'Gluten-Free',
    'Dairy-Free', 'Keto', 'Paleo', 'Halal'
  ];

  const allergyOptions = [
    'Nuts', 'Peanuts', 'Shellfish', 'Fish', 'Eggs', 'Soy', 'Wheat',
    'Dairy', 'Sesame', 'Sulfites'
  ];

  const cuisineOptions = [
    'Italian', 'Mexican', 'Chinese', 'Indian', 'Japanese',
    'Thai', 'Mediterranean', 'American', 'French', 'Greek'
  ];

  const dayOptions = [
    { value: 'monday', label: 'Mon' },
    { value: 'tuesday', label: 'Tue' },
    { value: 'wednesday', label: 'Wed' },
    { value: 'thursday', label: 'Thu' },
    { value: 'friday', label: 'Fri' },
    { value: 'saturday', label: 'Sat' },
    { value: 'sunday', label: 'Sun' }
  ];

  const mealOptions = [
    { value: 'breakfast', label: 'Breakfast' },
    { value: 'lunch', label: 'Lunch' },
    { value: 'dinner', label: 'Dinner' },
    { value: 'snack', label: 'Snack' }
  ];

  const getWeekOptions = () => {
    const today = new Date();
    const weeks = [];
    for (let i = 0; i < 4; i++) {
      const ws = startOfWeek(addWeeks(today, i), { weekStartsOn: 0 });
      const we = endOfWeek(addWeeks(today, i), { weekStartsOn: 0 });
      weeks.push({
        index: i,
        label: i === 0 ? 'This Week' : `Week ${i + 1}`,
        dateRange: `${format(ws, 'MMM d')} - ${format(we, 'MMM d')}`,
      });
    }
    return weeks;
  };

  const showSignInToast = () => {
    toast.error('Sign in to customize your preferences', { duration: 2000, icon: '🔒' });
  };

  if (!isOpen) return null;

  return (
    <div className="meal-planify-inline">
      <div className="meal-planify-inline-header">
        <div className="inline-title-row">
          <h2><i className="fas fa-magic"></i> MealPlanify</h2>
          <p className="inline-subtitle">Preview — Sign in to generate a personalized meal plan</p>
        </div>
        <button className="meal-planify-inline-close" onClick={onClose}>
          <i className="fas fa-times"></i> Cancel
        </button>
      </div>

      <div className="preferences-section">
        <h3>Dietary Preferences</h3>
        <div className="preference-grid">
          {dietaryOptions.map(option => (
            <label key={option} className="preference-checkbox preview-checkbox">
              <input
                type="checkbox"
                checked={preferences.dietaryPreferences.includes(option.toLowerCase().replace(/\s+/g, '-'))}
                onChange={showSignInToast}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="preferences-section">
        <h3>Allergies</h3>
        <div className="preference-grid">
          {allergyOptions.map(option => (
            <label key={option} className="preference-checkbox preview-checkbox">
              <input
                type="checkbox"
                checked={preferences.allergies.includes(option.toLowerCase())}
                onChange={showSignInToast}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="preferences-section">
        <h3>Cuisine Preferences</h3>
        <div className="preference-grid">
          {cuisineOptions.map(option => (
            <label key={option} className="preference-checkbox preview-checkbox">
              <input
                type="checkbox"
                checked={preferences.cuisinePreferences.includes(option.toLowerCase())}
                onChange={showSignInToast}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="preferences-section">
        <h3>Cost Range</h3>
        <div className="cost-options">
          {[
            { value: 'low', label: 'Budget-Friendly', icon: '💰' },
            { value: 'medium', label: 'Moderate', icon: '💳' },
            { value: 'high', label: 'Premium', icon: '💎' }
          ].map(option => (
            <label key={option.value} className="cost-option preview-option">
              <input
                type="radio"
                name="costRange"
                value={option.value}
                checked={preferences.costRange === option.value}
                onChange={showSignInToast}
              />
              <div className="cost-option-content">
                <span className="cost-icon">{option.icon}</span>
                <span>{option.label}</span>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="preferences-section">
        <h3>Meals to Include</h3>
        <div className="meal-options">
          {mealOptions.map(option => (
            <label key={option.value} className="meal-option preview-option">
              <input
                type="checkbox"
                checked={preferences.mealsToConsider.includes(option.value)}
                onChange={showSignInToast}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="preferences-section">
        <h3>Days to Plan</h3>
        <div className="day-options">
          {dayOptions.map(option => (
            <label key={option.value} className="day-option preview-option">
              <input
                type="checkbox"
                checked={preferences.daysToPlan.includes(option.value)}
                onChange={showSignInToast}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="preferences-section">
        <h3>Weeks to Plan</h3>
        <div className="weeks-selector">
          {getWeekOptions().map(week => (
            <label key={week.index} className="week-option preview-option">
              <input
                type="checkbox"
                checked={preferences.selectedWeeks.includes(week.index)}
                onChange={showSignInToast}
              />
              <div className="week-option-content">
                <span className="week-label">{week.label}</span>
                <span className="week-dates">{week.dateRange}</span>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="preferences-section">
        <h3>Additional Options</h3>
        <div className="additional-options">
          <label className="additional-option preview-option">
            <input type="checkbox" checked={preferences.mealPrepRecipes} onChange={showSignInToast} />
            <span>Prefer Meal Prep Recipes</span>
          </label>
          <label className="additional-option preview-option">
            <input type="checkbox" checked={preferences.lowCarb} onChange={showSignInToast} />
            <span>Low Carb Focus</span>
          </label>
          <label className="additional-option preview-option">
            <input type="checkbox" checked={preferences.highProtein} onChange={showSignInToast} />
            <span>High Protein Focus</span>
          </label>
        </div>
      </div>

      <div className="preferences-section">
        <h3>Spice Level</h3>
        <SpiceLevelPicker
          value={preferences.spiceLevel}
          onChange={showSignInToast}
        />
      </div>

      <div className="meal-planify-inline-footer">
        <button className="save-preferences-btn preview-btn" onClick={() => toast.error('Sign in to save preferences', { icon: '🔒' })}>
          <i className="fas fa-save"></i> Save Preferences
        </button>
        <button className="generate-meal-plan-btn preview-btn" onClick={() => toast.error('Sign in to generate your personalized meal plan', { duration: 3000, icon: '🔒' })}>
          <i className="fas fa-magic"></i> Generate Meal Plan
        </button>
      </div>
    </div>
  );
};

export default MealPlanifyModalPreview;
